// src/onboarding/ui/PersonalizationForm.jsx
//
// v196 (BE 571ac04 + 2ad8bdf) — THE personalization form. One POST starts the run; nothing is saved before it
// (a Skip loses what was typed — contract). Order, top to bottom: name · sector · documents · website
// (documents before the website: the uploads must finish before the submit).
//  • Name: PATCH /tenant-admin/tenant FIRST, only when changed; a refused rename sends nothing else;
//    renameTenantLocally only on a 200 (RenameWorkspaceDialog's order).
//  • Sector: pre-filled ONCE from the status read's `form.sector`; sent only when it differs (sectorToSend). An
//    emptied pre-filled sector cannot be cleared — the hint says the recorded one stays in use.
//  • Documents: the shared context/DocumentPicker (explicit list, ≤ 10). ⛔ No indexing wait.
//  • Website: OPTIONAL; pre-filled from GET /website-site unless `is_sentinel`; ⛔ NOT saved on Skip.
//  • A line when the typed website would REPLACE the connected site; a warning line (never a block) when every
//    ticked document has no extracted text and there is no website.
//  • A source is required (website or ≥ 1 document); a sector alone is not one.
//  • ⭐ The ONE warning (decision 2026-10-02): documents + no website + allows_reruns === false → a confirm
//    dialog. ⛔ No general pre-start warning (product departure since 2026-09-30); ⛔ no cost copy.
//    The dialog is rendered OUTSIDE the <form> for safety (AdminModal does not portal; its buttons are
//    `type="button"` since the v196 fix, but nothing inside a form should be able to submit it by accident).
//  • ⛔ SUBMIT BY BUTTON ONLY (decision 2026-10-02): Enter in a text field (name, sector, website, the picker's
//    search) never submits — the POST starts a long run that cannot be undone, and on a one-run plan it is the
//    workspace's only one.
//  • 202 → writePendingStart(baseline) + requestOnboardingRefresh(): the page follows the store.
//  • Refusals: billing → RunRefusalNotice; RUN_ACTIVE → refresh, no error; DOCUMENT_NOT_FOUND → untick the
//    missing files and re-list; everything else → copy.formErrorCopy. ⛔ Never the server's own text.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext.jsx';
import { renameTenant } from '../../tenantAdmin/tenantAdminApi.js';
import { getWebsiteSite } from '../../context/contextApi.js';
import ConfirmDialog from '../../admin/components/ConfirmDialog.jsx';
import RunRefusalNotice from '../../components/credits/RunRefusalNotice.jsx';
import { refusalFromError } from '../../utils/runRefusal.js';
import { startPersonalization } from '../onboardingApi.js';
import {
  DISPLAY_NAME_MAX, SECTOR_MAX, WEBSITE_MAX, connectedSiteOf, sameSite, websiteProblem, hasSource,
  needsDocsOnlyWarning, recordedSectorOf, sectorToSend, allTicksTextless, isValidWebsite,
} from '../onboardingModel.js';
import { writePendingStart } from '../onboardingStorage.js';
import { requestOnboardingRefresh } from '../onboardingStore.js';
import { formErrorCopy, renameErrorCopy } from '../copy.js';
import DocumentPicker from '../../context/DocumentPicker.jsx';
import { COLORS, SEMANTIC } from '../../constants/theme.js';
import { LABEL, INPUT, HINT, FIELD, PRIMARY, SECONDARY, NOTE, ACTIONS } from './formStyles.js';

const H3 = { fontSize: '15px', margin: '0 0 4px', color: COLORS.ink };

export default function PersonalizationForm({ tenantId, status, baseline, onSkip }) {
  const { t } = useTranslation('onboarding');
  const { activeTenant, renameTenantLocally } = useAuth();
  const originalName = activeTenant?.display_name || '';

  const [name, setName] = useState(originalName);
  const [sectorPrefill] = useState(() => recordedSectorOf(status));   // ONCE — never follows the store
  const [sector, setSector] = useState(sectorPrefill);
  const [selected, setSelected] = useState([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerReload, setPickerReload] = useState(0);
  const [pickerDocs, setPickerDocs] = useState([]);                    // the picker's list, for the textless hint
  const [connected, setConnected] = useState('');
  const [website, setWebsite] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // 422 WEBSITE_UNRESOLVABLE: shown under the website field (contract), cleared when the field is edited.
  const [siteError, setSiteError] = useState('');
  const [refusal, setRefusal] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const editedRef = useRef(false);
  const inFlightRef = useRef(false);   // never double-submit: the POST reads the documents synchronously

  // Website pre-fill (unchanged from v191). A failed read just leaves the field empty.
  useEffect(() => {
    let alive = true;
    getWebsiteSite({ tenant: tenantId })
      .then((read) => {
        if (!alive) return;
        const site = connectedSiteOf(read);
        setConnected(site);
        if (site && !editedRef.current) setWebsite(site);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [tenantId]);

  const onPickerChange = useCallback((next) => setSelected(next), []);

  const trimmedName = name.trim();
  const nameInvalid = trimmedName.length === 0 || trimmedName.length > DISPLAY_NAME_MAX;
  const siteProblem = websiteProblem(website);
  const showInvalid = touched && siteProblem === 'invalid';
  const replacesSite = !!connected && isValidWebsite(website) && !sameSite(website, connected);
  const source = hasSource(website, selected);
  const canSubmit = !busy && !pickerBusy && !nameInvalid && !siteProblem && source;
  const sectorKept = !!sectorPrefill && !sector.trim();

  async function run() {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    setError('');
    setSiteError('');
    setRefusal(null);
    try {
      if (trimmedName !== originalName.trim()) {
        try {
          const res = await renameTenant(tenantId, trimmedName);
          renameTenantLocally(tenantId, res?.display_name || trimmedName);
        } catch (err) {
          setError(renameErrorCopy(t, err));
          setBusy(false);
          return;
        }
      }
      try {
        await startPersonalization({
          tenant: tenantId, website, sector: sectorToSend(sectorPrefill, sector), documents: selected,
        });
        writePendingStart(tenantId, baseline);
        requestOnboardingRefresh();
      } catch (err) {
        setBusy(false);
        if (err?.code === 'RUN_ACTIVE') { requestOnboardingRefresh(); return; }
        if (err?.code === 'DOCUMENT_NOT_FOUND') {
          const missing = Array.isArray(err?.details?.documents) ? err.details.documents : [];
          setSelected((prev) => prev.filter((n) => !missing.includes(n)));
          setPickerReload((n) => n + 1);
        }
        if (err?.code === 'WEBSITE_UNRESOLVABLE') { setSiteError(t('errors.websiteUnresolvable')); return; }
        const billing = refusalFromError(err);
        if (billing) { setRefusal(billing); return; }
        setError(formErrorCopy(t, err));
      }
    } finally {
      inFlightRef.current = false;
    }
  }

  // ⛔ Enter in an <input> must not submit the form (implicit submission): only the button starts the run.
  const blockEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) e.preventDefault();
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    if (needsDocsOnlyWarning(status, website, selected)) { setConfirmOpen(true); return; }
    run();
  };

  return (
    <>
      <form onSubmit={submit} onKeyDown={blockEnterSubmit} noValidate>
        <div style={FIELD}>
          <label htmlFor="ob-name" style={LABEL}>{t('form.nameLabel')}</label>
          <input
            id="ob-name" style={INPUT(nameInvalid)} value={name} maxLength={DISPLAY_NAME_MAX}
            onChange={(e) => setName(e.target.value)} disabled={busy} aria-invalid={nameInvalid}
          />
          <p style={HINT}>{t('form.nameHint')}</p>
        </div>

        <div style={FIELD}>
          <label htmlFor="ob-sector" style={LABEL}>{t('form.sectorLabel')}</label>
          <input
            id="ob-sector" style={INPUT()} value={sector} maxLength={SECTOR_MAX}
            placeholder={t('form.sectorPlaceholder')} onChange={(e) => setSector(e.target.value)} disabled={busy}
            aria-describedby="ob-sector-hint"
          />
          <p id="ob-sector-hint" style={HINT}>
            {sectorKept ? t('form.sectorKept', { sector: sectorPrefill }) : t('form.sectorHint')}
          </p>
        </div>

        <section aria-labelledby="ob-docs-title" style={{ marginBottom: '28px' }}>
          <h3 id="ob-docs-title" style={H3}>{t('form.docsTitle')}</h3>
          <p style={{ ...HINT, margin: '0 0 12px' }}>{t('form.docsIntro')}</p>
          <DocumentPicker
            tenantId={tenantId} label={t('form.docsTitle')} selected={selected} onChange={onPickerChange}
            disabled={busy} reloadToken={pickerReload} onBusyChange={setPickerBusy} onDocsChange={setPickerDocs}
          />
        </section>

        <h3 style={H3}>{t('form.websiteTitle')}</h3>
        <div style={FIELD}>
          <label htmlFor="ob-website" style={LABEL}>{t('form.websiteLabel')}</label>
          <input
            id="ob-website" type="url" style={INPUT(showInvalid)} value={website} maxLength={WEBSITE_MAX}
            placeholder={t('form.websitePlaceholder')} disabled={busy} aria-invalid={showInvalid || !!siteError}
            aria-describedby={siteError ? 'ob-website-error' : undefined}
            onChange={(e) => { editedRef.current = true; setWebsite(e.target.value); setSiteError(''); }}
            onBlur={() => setTouched(true)}
          />
          {showInvalid && <p role="alert" style={{ ...HINT, color: SEMANTIC.errore.fg }}>{t('form.websiteInvalid')}</p>}
          {siteError && <p id="ob-website-error" role="alert" style={{ ...HINT, color: SEMANTIC.errore.fg }}>{siteError}</p>}
          {replacesSite && <p style={{ ...HINT, color: SEMANTIC.attenzione.fg }}>{t('form.replacesSite', { site: connected })}</p>}
        </div>

        {!source && <p role="status" style={HINT}>{t('form.needSource')}</p>}
        {/* A HINT, never a block: the run uses its own text extractor, not the KB index. */}
        {!isValidWebsite(website) && allTicksTextless(pickerDocs, selected) && (
          <p role="status" style={{ ...HINT, color: SEMANTIC.attenzione.fg }}>{t('form.noTextWarning')}</p>
        )}
        {refusal && (
          <div style={{ marginBottom: '16px' }}>
            <RunRefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} />
          </div>
        )}
        {error && <div role="alert" style={NOTE(SEMANTIC.errore)}>{error}</div>}

        <div style={ACTIONS}>
          <button type="submit" disabled={!canSubmit} style={PRIMARY(!canSubmit)}>
            {busy ? t('form.submitting') : t('form.submit')}
          </button>
          <button type="button" onClick={onSkip} style={SECONDARY}>{t('page.skip')}</button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => { await run(); }}
        title={t('form.docsOnly.title')}
        message={t('form.docsOnly.message')}
        confirmLabel={t('form.docsOnly.confirm')}
      />
    </>
  );
}
